import { AutoComplete, Input } from 'antd';
import { useState } from 'react';
import { toTwelveHour } from '@pams/shared';
import { parseTimeInput, timeSuggestions } from '@/lib/timeOfDay';
import { cn } from '@/lib/utils';
import { notifyDevChange } from '@/lib/devNotice';

/**
 * ─── LAYER: component (shared intake) ────────────────────────────────────────────
 *
 * A TIME OF DAY, TYPED OR PICKED.
 *
 * ⚠️ IT COMMITS ON BLUR, NOT ON EVERY KEYSTROKE, AND THAT IS THE WHOLE POINT.
 *
 * The field it replaces was a plain `Input` bound straight to the form, so `2` on its way to
 * `23` reached the store as a value the schema refuses and the shift engine cannot read — a
 * half-typed entry becoming a real one. Here the raw text is LOCAL state; the form only ever
 * receives a canonical `HH:mm` or the empty string, so nothing downstream sees a fragment.
 *
 * ⚠️ AND `HH:mm` IS THE ONLY THING IT EMITS. `slot.util.ts` compares times as STRINGS and
 * that soundness rests entirely on the zero padding — `8:00` would sort before `20:00` and
 * an overnight shift would stop being overnight. The AM/PM beneath the field is for the
 * operator and is never stored, never sent and never compared.
 *
 * ⚠️ [amended P4-UX] IT IS THE MERIDIEM ALONE, NOT A SECOND CLOCK. It used to read the whole
 * 12-hour time, so `00:00` carried `12:00 AM` under it — a line spent repeating digits the
 * operator is already looking at, and two different numbers for one value on a screen full of
 * times. `lib/timeOfDay.ts` has the reasoning.
 *
 * Typing and picking both work: `8`, `0800`, `8pm` and `23` are all read
 * (`lib/timeOfDay.ts`), and the dropdown offers every half hour, narrowed to what has been
 * typed so far.
 */
export type TimeFieldProps = {
  /** Canonical `HH:mm`, or `''`. */
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
  'data-testid'?: string;
};

/**
 * ⚠️ [C1d §3b] WHAT THE FIELD ACCEPTS, NOT TWO OF THE TWELVE FORMS IT ACCEPTS.
 *
 * It read `e.g. 8 or 20:00`, which is not wrong and is misleading in the one way that matters:
 * it implies a 24-hour entry needs a colon. `parseTimeInput` reads a bare `16` as 16:00 and
 * displays it as 4:00 PM — correct, and indistinguishable from a bug to somebody who has just
 * been told the format is `20:00`. So the field was right and the hint was wrong.
 *
 * ⚠️ SHORT, BECAUSE THE ONE PLACE IT RENDERS IS NARROW. The shift rows override it with
 * `From`/`To`; the only site that shows this string is the duty point card's Reporting time,
 * which is the 20% column of a 60/20/20 band.
 *
 * ⚠️ AND IT IS PINNED BY A TEST THAT PARSES ITS OWN EXAMPLES. A placeholder describing what a
 * field accepts is a CLAIM, and the cheapest way for it to go wrong again is an edit that
 * sounds better and parses worse — so `TimeField.test.tsx` runs every example in this string
 * through `parseTimeInput` rather than trusting it.
 */
export const TIME_FIELD_PLACEHOLDER = 'e.g. 16 or 4:30pm';

export const TimeField = ({
  value = '',
  onChange,
  disabled,
  placeholder = TIME_FIELD_PLACEHOLDER,
  'aria-label': ariaLabel,
  className,
  'data-testid': testId,
}: TimeFieldProps) => {
  /**
   * What is being typed, while it is being typed. `undefined` means "show the value".
   *
   * ⚠️ [P4-UX.2] THE FIELD DISPLAYS 12-HOUR; THE FORM STILL HOLDS `HH:mm`. `value` is
   * canonical and `onChange` only ever emits canonical — what changes is the string in the
   * box while the operator is NOT typing into it. `parseTimeInput` reads `5:00 PM` back
   * (it strips whitespace, takes the meridiem suffix, then the `h:mm` body), so the round
   * trip holds and typing `17:00` still works.
   */
  const [typing, setTyping] = useState<string | undefined>();
  const shown = typing ?? toTwelveHour(value);

  const commit = (raw: string): void => {
    setTyping(undefined);
    if (raw.trim() === '') {
      onChange?.('');
      return;
    }
    const parsed = parseTimeInput(raw);
    /*
     * ⚠️ AN UNREADABLE ENTRY REVERTS RATHER THAN CLEARING. Clearing throws away a value the
     * operator had already got right, on a typo they can see.
     */
    if (parsed === null) {
      notifyDevChange({
        where: ariaLabel ?? 'Time',
        what: `"${raw}" is not a time — kept ${value || 'empty'}`,
        why: 'Clearing it instead would throw away a value that was already right because of a typo the operator can see and fix.',
      });
    } else if (parsed !== raw.trim()) {
      notifyDevChange({
        where: ariaLabel ?? 'Time',
        what: `"${raw}" → ${parsed}`,
        why: 'Times are stored and compared as zero-padded 24-hour strings — `8:00` would sort before `20:00` and an overnight shift would stop being overnight.',
      });
    }
    onChange?.(parsed ?? value);
  };

  return (
    <div className={cn('flex flex-col', className)}>
      {/*
        ⚠️ THE `aria-label` GOES ON A CHILD `Input`, NOT ON `AutoComplete`.
        AntD spreads an unknown prop onto its WRAPPER as well as its input, so passing it to
        `AutoComplete` puts the same accessible name on two elements and every
        `getByLabelText('Shift 1 start')` in the suite throws "found multiple elements".
        One control, one name.
      */}
      <AutoComplete
        value={shown}
        disabled={disabled}
        data-testid={testId}
        className="w-full"
        // The list is short and pre-filtered by `timeSuggestions`; AntD must not filter again.
        filterOption={false}
        options={timeSuggestions(shown).map((time) => ({
          value: time,
          label: toTwelveHour(time),
        }))}
        onChange={setTyping}
        onSelect={commit}
        onBlur={() => {
          commit(shown);
        }}
        /*
         * ⚠️ [UX-FINAL] ESCAPE REVERTS. IT NEVER COMMITS.
         *
         * The recorded defect said AntD clears the box on Escape. It does not — MEASURED
         * against rc-select@14.16.8, whose ESC case is `toggleOpen(false)` and nothing else
         * (`lib/OptionList.js:243`), while the combobox branch of its search-value sync
         * (`lib/Select.js:240`) re-reads the controlled `value` and never blanks it.
         *
         * What was really happening is quieter and worse. Escape did NOTHING here, so the
         * half-typed text stayed in `typing` and the `onBlur` above then committed the
         * fragment the operator believed they had just discarded: a field holding `20:00`,
         * typed down to `2`, cancelled and tabbed out of, became `02:00`. Dropping `typing`
         * restores the committed value, and the blur that follows re-commits that same
         * value — a no-op — instead of the fragment.
         *
         * ⚠️ ON `AutoComplete`, NOT ON THE CHILD `Input`. rc-select owns the input's own
         * `onKeyDown` and forwards to this prop AFTERWARDS, unconditionally, whether or not
         * the popup was open (`lib/BaseSelect/index.js:330`) — so an `onKeyDown` on the
         * `Input` below would be overwritten. It is also a prop rc-select DESTRUCTURES, so
         * unlike `aria-label` it is never spread onto the wrapper as a DOM attribute.
         *
         * Propagation is deliberately left alone: rc-select already stops it when the popup
         * was open, and both call sites are full pages with no dialog to dismiss.
         */
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          setTyping(undefined);
        }}
      >
        <Input aria-label={ariaLabel} placeholder={placeholder} />
      </AutoComplete>
      {/*
        ⚠️ [P4-UX.2] THE CAPTION IS GONE, AND ITS ABSENCE IS THE FIX.

        It read the meridiem alone under a field showing `17:00`, so the two rendered as
        "17:00 PM" — a string that is not a time in any notation. The field itself now says
        "5:00 PM", which is what the caption was there to supply.
      */}
    </div>
  );
};
